import React from 'react';
import type { CFTopEntry, LoanSelfFunding, CFMoneyFlowPayload, CFFlowOutRow, CFFlowInRow } from '../../types/campaignFinance';
import { fmtMoney } from '../../utils/campaignFinanceUtils';
import CommitteeLink from '../committee/CommitteeLink';

/** Entity clicks resubmit the search as that entity — the panel is the navigation map. */
export type CFEntitySearchHandler = (
  kind: 'contributor' | 'committee' | 'payee',
  name: string,
  meta?: { vendorId?: string | null },
) => void;

interface CFMoneyFlowProps {
  committeeName: string;
  topContributors: CFTopEntry[];
  topPayees: CFTopEntry[];
  totalIn: number;
  totalOut: number;
  selfFunding: LoanSelfFunding | null;
  /**
   * Self-describing payload from /committees/:co_id/money-flow. When present the panel
   * renders badges, depth-1 previews, and disclosure states from it — all computed
   * server-side (the UI renders disclosure, it never computes it). When null (e.g. a
   * name-scoped committee that never resolved a co_id) the panel falls back to the
   * legacy client-side aggregates below.
   */
  flow?: CFMoneyFlowPayload | null;
  /**
   * Every entity name on the panel navigates: donors → contributor view, badged
   * committees → committee view, terminal payees → vendor view (carrying the resolved
   * vendor_id when the row has one). Absent = plain text.
   */
  onEntitySearch?: CFEntitySearchHandler;
}

/* ───────────────────────────── shared bits ───────────────────────────── */

const Bar: React.FC<{ pct: number; side: 'in' | 'out' }> = ({ pct, side }) => (
  <div className={`h-1.5 rounded-full overflow-hidden ${side === 'in' ? 'bg-cf-in-soft' : 'bg-cf-out-soft'}`}>
    <div
      className="h-full rounded-full"
      style={{ width: `${Math.max(2, Math.min(100, pct))}%`, background: side === 'in' ? 'var(--cf-in)' : 'var(--cf-out)' }}
    />
  </div>
);

/** Counterparty chip. Every label and hedge originates in the payload's badge fields. */
const Chip: React.FC<{ variant: 'committee' | 'former' | 'endpoint' | 'unmatched' | 'reciprocal'; label: string; title?: string }> = ({ variant, label, title }) => {
  const cls =
    variant === 'committee' ? 'bg-cf-out-soft text-cf-out-text'
    : variant === 'former' ? 'bg-cf-out-soft text-cf-out-text opacity-80'
    : variant === 'reciprocal' ? 'bg-cf-loan-soft text-cf-loan'
    : variant === 'unmatched' ? 'border border-dashed border-border-strong dark:border-border-dark text-text-tertiary dark:text-text-dark-tertiary'
    : 'border border-border-light dark:border-border-dark text-text-secondary dark:text-text-dark-secondary';
  return (
    <span title={title} className={`rounded-full px-2 py-[1px] text-[10px] font-semibold whitespace-nowrap flex-none ${cls}`}>
      {label}
    </span>
  );
};

/* ─────────────────────────── payload rendering ───────────────────────── */

interface OutRowProps {
  row: CFFlowOutRow;
  maxTotal: number;
  onEntitySearch?: CFEntitySearchHandler;
}

const OutRow: React.FC<OutRowProps> = ({ row, maxTotal, onEntitySearch }) => {
  const isCommittee = row.kind === 'committee';
  const reciprocal = (row.network?.reciprocal_total ?? 0) > 0;
  const nameCls = 'text-[12.5px] text-text-primary dark:text-text-dark-primary truncate';
  return (
    <div>
      <div className="flex justify-between items-baseline mb-[3px] gap-2">
        <span className="flex items-baseline gap-1.5 min-w-0 flex-1">
          {isCommittee ? (
            // A committee payee opens the committee card; its "Open full dashboard" is the
            // profile → counterparty hop (PRD §5.12, O3).
            <CommitteeLink coId={row.committee?.co_id} name={row.name} className={`${nameCls} hover:text-primary-700 dark:hover:text-primary-dark-400`} />
          ) : !isCommittee && onEntitySearch ? (
            // Terminal payee → the vendor view, carrying the resolved identity when the
            // row has one (unresolved payees still navigate; the view states the fallback).
            <button type="button" onClick={() => onEntitySearch('payee', row.name, { vendorId: row.vendor_id })} className={`${nameCls} text-left hover:text-primary-700 dark:hover:text-primary-dark-400 hover:underline`} title={`Open payee ${row.name}`}>
              {row.name}
            </button>
          ) : (
            <span className={nameCls} title={row.name}>{row.name}</span>
          )}
          {row.badge === 'committee' && <Chip variant="committee" label="Committee" />}
          {row.badge === 'committee_former_name' && <Chip variant="former" label="Committee ~" title={row.committee?.caveat} />}
          {row.badge === null && <Chip variant="endpoint" label={row.kind === 'individual' ? 'Individual' : 'Vendor'} />}
          {reciprocal && <Chip variant="reciprocal" label="↺" title="Reciprocal transfers — money moves in both directions" />}
        </span>
        <span className="tabular-nums text-xs font-semibold flex-none text-primary-700 dark:text-primary-dark-400">
          {fmtMoney(row.total, { compact: true })}
        </span>
      </div>
      <Bar pct={(row.total / maxTotal) * 100} side="out" />
      {/* Depth-1 context, straight from the payload. */}
      {isCommittee && row.network && (row.network.onward_total > 0 || reciprocal) && (
        <div className="text-[11px] text-text-tertiary dark:text-text-dark-tertiary mt-[3px]">
          {row.network.onward_total > 0 && <>Sent {fmtMoney(row.network.onward_total, { compact: true })} onward to {row.network.onward_committees} committee{row.network.onward_committees === 1 ? '' : 's'}</>}
          {row.network.onward_total > 0 && reciprocal && ' · '}
          {reciprocal && <>also sent {fmtMoney(row.network.reciprocal_total, { compact: true })} back to this committee</>}
        </div>
      )}
      {!isCommittee && row.rollup && row.rollup.committees_paying > 0 && (
        <div className="text-[11px] text-text-tertiary dark:text-text-dark-tertiary mt-[3px]">
          Paid by {row.rollup.committees_paying} committee{row.rollup.committees_paying === 1 ? '' : 's'} · {fmtMoney(row.rollup.total_all_committees, { compact: true })} total in window
        </div>
      )}
      {!isCommittee && !row.rollup && row.disclosure && (
        <div className="text-[11px] text-text-tertiary dark:text-text-dark-tertiary mt-[3px]">{row.disclosure}</div>
      )}
    </div>
  );
};

const InRow: React.FC<{ row: CFFlowInRow; maxTotal: number; onEntitySearch?: CFEntitySearchHandler }> = ({ row, maxTotal, onEntitySearch }) => (
  <div>
    <div className="flex justify-between items-baseline mb-[3px] gap-2">
      <span className="flex items-baseline gap-1.5 min-w-0 flex-1">
        {onEntitySearch ? (
          // Donor → contributor view. Committee-typed donors route here too: with no
          // contributor→co_id resolution, the contributor view is the only claim the
          // data supports (the row already carries its unmatched disclosure).
          <button type="button" onClick={() => onEntitySearch('contributor', row.name)} className="text-[12.5px] text-text-primary dark:text-text-dark-primary truncate text-left hover:text-cf-in-text hover:underline" title={`Open contributor ${row.name}`}>
            {row.name}
          </button>
        ) : (
          <span className="text-[12.5px] text-text-primary dark:text-text-dark-primary truncate" title={row.name}>{row.name}</span>
        )}
        {row.kind === 'individual' && <Chip variant="endpoint" label="Individual" />}
        {row.kind === 'committee_unmatched' && <Chip variant="unmatched" label="Unmatched" title={row.disclosure} />}
        {row.kind === 'other' && row.reported_type && <Chip variant="endpoint" label={row.reported_type} />}
      </span>
      <span className="tabular-nums text-xs font-semibold flex-none text-cf-in-text">{fmtMoney(row.total, { compact: true })}</span>
    </div>
    <Bar pct={(row.total / maxTotal) * 100} side="in" />
    {row.disclosure && (
      <div className="text-[11px] text-text-tertiary dark:text-text-dark-tertiary mt-[3px]">{row.disclosure}</div>
    )}
  </div>
);

const PayloadFlow: React.FC<{ flow: CFMoneyFlowPayload; selfLoan: number; selfPct: number | null | undefined; onEntitySearch?: CFEntitySearchHandler }> = ({ flow, selfLoan, selfPct, onEntitySearch }) => {
  const maxIn = Math.max(1, ...flow.money_in.top.map(r => r.total));
  const maxOut = Math.max(1, ...flow.money_out.top.map(r => r.total));
  const pct = (p: number | null) => (p != null ? `${Math.round(p * 100)}%` : null);

  return (
    <>
      {/* Compact totals strip — replaces the legacy center node. */}
      <div className="flex items-center justify-center gap-3 rounded-[10px] bg-background-tertiary dark:bg-background-dark-tertiary px-3 py-2 mb-4 flex-wrap">
        <span className="text-xs">In <strong className="tabular-nums text-cf-in-text">{fmtMoney(flow.totals.raised, { compact: true })}</strong></span>
        <span className="font-semibold text-[13px] text-text-primary dark:text-text-dark-primary truncate max-w-[45%]" title={flow.committee.name}>{flow.committee.name}</span>
        <span className="text-xs">Out <strong className="tabular-nums text-primary-700 dark:text-primary-dark-400">{fmtMoney(flow.totals.spent, { compact: true })}</strong></span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[18px]">
        {/* Money in */}
        <div>
          <div className="flex items-center gap-[7px] mb-3 flex-wrap">
            <span className="w-2 h-2 rounded-full bg-cf-in" />
            <span className="text-xs font-semibold text-cf-in-text">Money in · top donors</span>
            {pct(flow.money_in.shown_pct) && (
              <span className="text-[10.5px] text-text-tertiary dark:text-text-dark-tertiary">top {flow.money_in.top.length} · {pct(flow.money_in.shown_pct)} of raised</span>
            )}
          </div>
          <div className="flex flex-col gap-2.5">
            {flow.money_in.top.length === 0 && <div className="text-[11.5px] text-accent-400">No contributions in range.</div>}
            {flow.money_in.top.map(r => <InRow key={r.name} row={r} maxTotal={maxIn} onEntitySearch={onEntitySearch} />)}
            {selfLoan > 0 && (
              <div className="mt-[3px] pt-[9px] border-t border-dashed border-cf-loan-border">
                <div className="flex justify-between mb-[3px]">
                  <span className="text-[12.5px] text-cf-loan">Self-loan · candidate{selfPct != null ? ` · ${selfPct}%` : ''}</span>
                  <span className="tabular-nums text-xs font-semibold text-cf-loan">{fmtMoney(selfLoan, { compact: true })}</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden bg-cf-loan-soft">
                  <div className="h-full w-full" style={{ background: 'repeating-linear-gradient(45deg,var(--cf-loan),var(--cf-loan) 4px,var(--cf-loan-light) 4px,var(--cf-loan-light) 8px)' }} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Money out */}
        <div>
          <div className="flex items-center gap-[7px] mb-3 flex-wrap">
            <span className="w-2 h-2 rounded-full bg-cf-out" />
            <span className="text-xs font-semibold text-primary-700 dark:text-primary-dark-400">Money out · top recipients</span>
            {pct(flow.money_out.shown_pct) && (
              <span className="text-[10.5px] text-text-tertiary dark:text-text-dark-tertiary">top {flow.money_out.top.length} · {pct(flow.money_out.shown_pct)} of spend</span>
            )}
          </div>
          <div className="flex flex-col gap-2.5">
            {flow.money_out.top.length === 0 && <div className="text-[11.5px] text-accent-400">No expenditures in range.</div>}
            {flow.money_out.top.map(r => (
              <OutRow key={`${r.kind}:${r.name}`} row={r} maxTotal={maxOut} onEntitySearch={onEntitySearch} />
            ))}
          </div>
        </div>
      </div>

      {/* Legend — the chip vocabulary, so the tree's ↺ is already familiar when it ships. */}
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap mt-4 pt-3 border-t border-border-light dark:border-border-dark text-[10.5px] text-text-tertiary dark:text-text-dark-tertiary">
        <span className="flex items-center gap-1.5"><Chip variant="committee" label="Committee" /> linked counterparty</span>
        <span>Individual · Vendor = endpoint</span>
        <span className="flex items-center gap-1.5"><Chip variant="reciprocal" label="↺" /> reciprocal transfers</span>
        <span className="flex items-center gap-1.5"><Chip variant="unmatched" label="Unmatched" /> shown as reported</span>
      </div>
    </>
  );
};

/* ─────────────────────────── legacy fallback ─────────────────────────── */

const LegacyRow: React.FC<{ name: string; amount: number; pct: number; side: 'in' | 'out' }> = ({ name, amount, pct, side }) => (
  <div>
    <div className="flex justify-between items-baseline mb-[3px] gap-2">
      <span className="text-[12.5px] text-text-primary dark:text-text-dark-primary truncate min-w-0 flex-1" title={name}>{name}</span>
      <span className={`tabular-nums text-xs font-semibold flex-none ${side === 'in' ? 'text-cf-in-text' : 'text-primary-700 dark:text-primary-dark-400'}`}>{fmtMoney(amount, { compact: true })}</span>
    </div>
    <Bar pct={pct} side={side} />
  </div>
);

const LegacyFlow: React.FC<{ committeeName: string; donors: CFTopEntry[]; vendors: CFTopEntry[]; totalIn: number; totalOut: number; selfLoan: number }> = ({ committeeName, donors, vendors, totalIn, totalOut, selfLoan }) => {
  const maxIn = Math.max(1, ...donors.map(d => d.total));
  const maxOut = Math.max(1, ...vendors.map(v => v.total));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_200px_1fr] gap-[18px] items-stretch">
      <div>
        <div className="flex items-center gap-[7px] mb-3">
          <span className="w-2 h-2 rounded-full bg-cf-in" />
          <span className="text-xs font-semibold text-cf-in-text">Money in · Top donors</span>
        </div>
        <div className="flex flex-col gap-2">
          {donors.length === 0 && <div className="text-[11.5px] text-accent-400">No contributions in range.</div>}
          {donors.map(d => <LegacyRow key={d.name} name={d.name} amount={d.total} pct={(d.total / maxIn) * 100} side="in" />)}
          {selfLoan > 0 && (
            <div className="mt-[3px] pt-[9px] border-t border-dashed border-cf-loan-border">
              <div className="flex justify-between mb-[3px]">
                <span className="text-[12.5px] text-cf-loan">Self-loan · candidate</span>
                <span className="tabular-nums text-xs font-semibold text-cf-loan">{fmtMoney(selfLoan, { compact: true })}</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden bg-cf-loan-soft">
                <div className="h-full w-full" style={{ background: 'repeating-linear-gradient(45deg,var(--cf-loan),var(--cf-loan) 4px,var(--cf-loan-light) 4px,var(--cf-loan-light) 8px)' }} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col items-center justify-center gap-2">
        <svg width="100%" height="22" viewBox="0 0 200 22" preserveAspectRatio="none" className="hidden lg:block">
          <line x1="2" y1="11" x2="186" y2="11" stroke="var(--cf-in)" strokeWidth="2" />
          <path d="M180 5 L192 11 L180 17" fill="none" stroke="var(--cf-in)" strokeWidth="2" />
        </svg>
        <div className="rounded-[14px] px-3 py-3.5 text-center w-full text-white" style={{ background: 'var(--cf-out-deep)', boxShadow: '0 6px 20px rgba(62,39,80,.3)' }}>
          <div className="text-[11px] font-semibold tracking-[0.04em] text-cf-out-pale">COMMITTEE</div>
          <div className="font-semibold text-[13px] leading-tight mt-[3px] truncate">{committeeName}</div>
          <div className="flex justify-center gap-3 mt-2.5">
            <div>
              <div className="text-[9px] font-semibold text-cf-out-muted">IN</div>
              <div className="font-bold text-sm text-cf-in-bright tabular-nums">{fmtMoney(totalIn, { compact: true })}</div>
            </div>
            <div className="w-px bg-white/20" />
            <div>
              <div className="text-[9px] font-semibold text-cf-out-muted">OUT</div>
              <div className="font-bold text-sm text-cf-out-mid tabular-nums">{fmtMoney(totalOut, { compact: true })}</div>
            </div>
          </div>
        </div>
        <svg width="100%" height="22" viewBox="0 0 200 22" preserveAspectRatio="none" className="hidden lg:block">
          <line x1="2" y1="11" x2="186" y2="11" stroke="var(--cf-out)" strokeWidth="2" />
          <path d="M180 5 L192 11 L180 17" fill="none" stroke="var(--cf-out)" strokeWidth="2" />
        </svg>
      </div>

      <div>
        <div className="flex items-center gap-[7px] mb-3 lg:justify-end">
          <span className="text-xs font-semibold text-primary-700 dark:text-primary-dark-400">Money out · Top vendors</span>
          <span className="w-2 h-2 rounded-full bg-cf-out" />
        </div>
        <div className="flex flex-col gap-2">
          {vendors.length === 0 && <div className="text-[11.5px] text-accent-400">No expenditures in range.</div>}
          {vendors.map(v => <LegacyRow key={v.name} name={v.name} amount={v.total} pct={(v.total / maxOut) * 100} side="out" />)}
        </div>
      </div>
    </div>
  );
};

/* ───────────────────────────── the panel ─────────────────────────────── */

/**
 * "Follow the money — both directions." With a `flow` payload: badge chips, depth-1
 * previews (vendor cross-committee rollups, committee onward dollars, reciprocal ↺),
 * visible unmatched state, and per-column coverage shares — all server-computed.
 * Without one: the legacy three-column snapshot.
 *
 * Deliberately shallow either way — no multi-hop here. The transfer-network tree is a
 * separate view this panel will link to once it exists (v1 spec: single destination,
 * two entry points).
 */
const CFMoneyFlow: React.FC<CFMoneyFlowProps> = ({ committeeName, topContributors, topPayees, totalIn, totalOut, selfFunding, flow, onEntitySearch }) => {
  const selfLoan = selfFunding && selfFunding.candidateOriginated > 0 ? selfFunding.candidateOriginated : 0;
  const selfPct = selfFunding?.candidatePctByAmount;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-accent-500 dark:text-text-dark-tertiary">
          Follow the money — both directions
        </span>
        {selfLoan > 0 && (
          <span className="rounded-full bg-cf-loan-soft text-cf-loan px-2.5 py-[3px] text-[11px] font-semibold">
            {selfPct != null ? `Self-funded ${selfPct}% · ` : ''}{fmtMoney(selfLoan, { compact: true })} candidate self-loan
          </span>
        )}
      </div>

      {flow
        ? <PayloadFlow flow={flow} selfLoan={selfLoan} selfPct={selfPct} onEntitySearch={onEntitySearch} />
        : (
          <LegacyFlow
            committeeName={committeeName}
            donors={topContributors.slice(0, 4)}
            vendors={topPayees.slice(0, 5)}
            totalIn={totalIn}
            totalOut={totalOut}
            selfLoan={selfLoan}
          />
        )}
    </div>
  );
};

export default CFMoneyFlow;
